import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../firebase/firebaseConfig';
import { ref, query, orderByChild, equalTo, get } from 'firebase/database';
import './VerificationPortal.css';

function VerificationPortal() {
  const { profileId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vId, setVId] = useState('');
  const [countdown, setCountdown] = useState(15);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      if (profileId === 'demo') {
          const demoData = {
              name: "Aditya Agarwal",
              profileId: "demo",
              suid: "DEMO_PORTAL_ACTIVE",
              avatar: null,
              githubUsername: "aditya2312"
          };
          setProfile(demoData);
          setVId("DVTR-0xEA2B-DEMO");
          setLoading(false);
          return;
      }

      try {
        const usersRef = ref(db, 'users');
        const q = query(usersRef, orderByChild('profileId'), equalTo(profileId));
        const snapshot = await get(q);

        if (snapshot.exists()) {
          const data = snapshot.val();
          const firstKey = Object.keys(data)[0];
          const profData = data[firstKey];
          setProfile(profData);
          
          // Generate a deterministic Hex ID for verification display
          const rawId = `${profData.profileId}-${profData.suid || 'v1'}`;
          let hash = 0;
          for (let i = 0; i < rawId.length; i++) {
            hash = ((hash << 5) - hash) + rawId.charCodeAt(i);
            hash |= 0;
          }
          const hexValue = Math.abs(hash).toString(16).toUpperCase();
          setVId(`DVTR-0x${hexValue}V${profData.suid ? profData.suid.slice(-4) : '70NE'}`);
        }
      } catch (err) {
        console.error("Verification fetch error:", err);
      }
      setLoading(false);
    };

    fetchProfile();
  }, [profileId]);

  // Countdown and Redirect Logic
  useEffect(() => {
    if (!loading && profile) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setVerified(true);
            // Open profile in new tab
            const url = profileId === 'demo' ? '/resume/demo' : `/dev/${profileId}`;
            window.open(url, '_blank');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [loading, profile, profileId]);

  if (loading) return (
    <div className="verify-loading">
      <div className="verify-spinner"></div>
      <p>Authenticating DevTracker Credentials...</p>
    </div>
  );

  if (!profile) return (
    <div className="verify-container">
      <div className="verify-card error glass-card">
        <i className="fa-solid fa-triangle-exclamation"></i>
        <h1>Verification Failed</h1>
        <p>This Profile ID is not registered in the DevTracker database or has been revoked.</p>
        <Link to="/" className="btn btn-primary">Return to Safety</Link>
      </div>
    </div>
  );

  return (
    <div className="verify-page">
      <div className="verify-container">
        <div className="verify-card glass-card">
          <div className={`verify-status-badge ${verified ? 'success' : 'pending'}`}>
            <i className={`fa-solid ${verified ? 'fa-shield-check' : 'fa-hourglass-half'}`}></i>
            <span>{verified ? 'Credential Fully Authenticated' : 'Performing Neural Audit...'}</span>
          </div>

          <div className="verify-profile-header">
            {profile.avatar ? (
              <img src={profile.avatar} alt="Profile" className="verify-avatar" />
            ) : (
              <div className="verify-avatar-placeholder"><i className="fa-solid fa-user"></i></div>
            )}
            <h1>{profile.name}</h1>
            <p className="verify-id-label">Official Profile ID: <strong>{profile.profileId}</strong></p>
          </div>

          <div className="verify-countdown-arena">
             <div className="countdown-ring">
                <svg viewBox="0 0 36 36">
                  <path className="ring-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  <path className="ring-progress" strokeDasharray={`${(countdown/15)*100}, 100`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                </svg>
                <div className="countdown-number">{countdown}</div>
             </div>
             <p className="countdown-note">{verified ? 'Redirecting to secure profile...' : 'Analyzing platform cross-signatures and security footprint.'}</p>
          </div>

          <div className="verify-hash-block">
            <label>Verification Hex ID</label>
            <div className="hash-box">
              <code>{vId}</code>
              <i className="fa-solid fa-copy" title="Copy ID"></i>
            </div>
          </div>

          <div className="verify-footer">
            <p>Verification protocol active. Do not close this terminal while the neural audit is in progress.</p>
            <button 
              onClick={() => {
                  const url = profileId === 'demo' ? '/resume/demo' : `/dev/${profileId}`;
                  window.open(url, '_blank');
              }} 
              className="btn btn-primary"
              disabled={!verified && countdown > 10} // Small delay to prevent instant skip
            >
              {verified ? 'Open Profile Again' : 'Manual Triage Access'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerificationPortal;
